'use client';

import { Box, Slider, Stack, Typography } from '@mui/material';
import { FilterSectionHeading } from '@odys/design-system/components/FilterSectionHeading';
import { NumberField } from '@odys/design-system/components/NumberField';
import { useLocale } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

import type { PriceHistogram } from '@/app/api/lib/products/product.pricing';
import { currencyAdornment } from '@/components/common/price-format';
import type { Currency } from '@/configurations/providers/CurrencyProvider';

type PriceRangeFilterProps = {
    value: [number, number] | null;
    onChange: (range: [number, number] | null) => void;
    histogram: PriceHistogram;
    currency: Currency;
    label: string;
    resetLabel: string;
};

export function PriceRangeFilter({
    value,
    onChange,
    histogram,
    currency,
    label,
    resetLabel,
}: PriceRangeFilterProps) {
    const locale = useLocale();
    const { symbol, position } = currencyAdornment(currency, locale);
    const active = value ?? [histogram.min, histogram.max];

    const maxCount = Math.max(...histogram.buckets, 1);
    const bucketCount = histogram.buckets.length;
    const range = histogram.max - histogram.min || 1;

    const commitRange = useCallback(
        (min: number, max: number) => {
            const clamped: [number, number] = [
                Math.max(histogram.min, Math.min(min, max)),
                Math.min(histogram.max, Math.max(min, max)),
            ];
            const isDefault = clamped[0] <= histogram.min && clamped[1] >= histogram.max;
            onChange(isDefault ? null : clamped);
        },
        [onChange, histogram.min, histogram.max],
    );

    const [localMin, setLocalMin] = useState(Math.round(active[0]));
    const [localMax, setLocalMax] = useState(Math.round(active[1]));

    useEffect(() => {
        setLocalMin(Math.round(active[0]));
        setLocalMax(Math.round(active[1]));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- resync de l'état local depuis les props uniquement quand value/bornes changent ; `active` est exclu volontairement (sinon resync à chaque édition locale).
    }, [value, histogram.min, histogram.max]);

    return (
        <Stack spacing={1} data-testid="dmc-products-price-filter">
            <Stack
                direction="row"
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <FilterSectionHeading>{label}</FilterSectionHeading>
                {value !== null && (
                    <Typography
                        variant="caption"
                        color="primary"
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => onChange(null)}
                        data-testid="dmc-products-price-filter-reset"
                    >
                        {resetLabel}
                    </Typography>
                )}
            </Stack>

            {/* Histogram + Slider dans un Box pour ne pas subir le spacing du Stack parent */}
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: '2px',
                        height: 56,
                        px: '13px',
                    }}
                    data-testid="dmc-products-price-histogram"
                >
                    {histogram.buckets.map((count, i) => {
                        const bucketMin = histogram.min + (i / bucketCount) * range;
                        const bucketMax = histogram.min + ((i + 1) / bucketCount) * range;
                        const isSelected =
                            value === null || (bucketMax >= active[0] && bucketMin <= active[1]);
                        return (
                            <Box
                                key={i}
                                sx={{
                                    flex: 1,
                                    height:
                                        count === 0
                                            ? 0
                                            : `${Math.max(8, (count / maxCount) * 100)}%`,
                                    bgcolor: isSelected ? 'primary.main' : 'action.disabled',
                                    borderRadius: '3px 3px 0 0',
                                    opacity: isSelected ? 0.35 : 0.15,
                                    transition: 'opacity 0.15s, background-color 0.15s',
                                }}
                            />
                        );
                    })}
                </Box>
                <Slider
                    value={[active[0], active[1]]}
                    min={histogram.min}
                    max={histogram.max}
                    onChange={(_, v) => {
                        const [min, max] = v as [number, number];
                        setLocalMin(Math.round(min));
                        setLocalMax(Math.round(max));
                        commitRange(min, max);
                    }}
                    disableSwap
                    sx={{ mt: '-13px' }}
                />
            </Box>

            {/* Min / Max inputs */}
            <Stack direction="row" spacing={0.5} sx={{ pt: 0.5 }}>
                <NumberField
                    label="Min"
                    value={localMin}
                    onValueChange={(v) => {
                        if (v !== null) {
                            setLocalMin(v);
                            commitRange(v, localMax);
                        }
                    }}
                    min={histogram.min}
                    max={localMax}
                    size="small"
                    startAdornment={position === 'start' ? symbol : undefined}
                    endAdornment={position === 'end' ? symbol : undefined}
                    sx={{ flex: 1 }}
                />
                <NumberField
                    label="Max"
                    value={localMax}
                    onValueChange={(v) => {
                        if (v !== null) {
                            setLocalMax(v);
                            commitRange(localMin, v);
                        }
                    }}
                    min={localMin}
                    max={histogram.max}
                    size="small"
                    startAdornment={position === 'start' ? symbol : undefined}
                    endAdornment={position === 'end' ? symbol : undefined}
                    sx={{ flex: 1 }}
                />
            </Stack>
        </Stack>
    );
}
